import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { BACKEND_URL } from '../utils/constants';
import { rememberFirmMfa } from '../utils/signInPolicy';

export type PlanTier = 'freemium' | 'pro' | 'max';

export type OrgRole = 'admin' | 'member' | 'viewer';

/** The caller's firm, from GET /api/org (enterprise batch 7). */
export interface OrgInfo {
  id: string;
  name: string;
  plan_tier: PlanTier;
  seat_limit: number | null;
  billing_mode: 'self_serve' | 'invoice';
  seats_used: number;
  pending_invitations: number;
  admin_count: number;
  my_role: OrgRole;
  // Sign-in policy (enterprise batch 10).
  require_mfa?: boolean;
  /** False until UnderwritRE's Auth0 plan supports two-factor (ENTERPRISE_MFA_ENABLED). */
  mfa_available?: boolean;
  sso_connection?: string | null;
  sso_enforced?: boolean;
  domains?: OrgDomainInfo[];
  /** How the current sign-in happened, from the access token. */
  my_sign_in?: { mfa: boolean; connection: string | null; action_claims: boolean };
}

export interface OrgDomainInfo {
  id: string;
  domain: string;
  auto_join: boolean;
  default_role: OrgRole;
}

export interface User {
  email: string;
  sub: string;
  plan_tier: PlanTier | null;
  [key: string]: any;
}

interface UserContextType {
  user: User | null;
  isAuthenticated: boolean;
  userLoaded: boolean;
  refreshUser: () => Promise<void>;
  org: OrgInfo | null;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [userDetails, setUserDetails] = useState<User | null>(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [org, setOrg] = useState<OrgInfo | null>(null);

  const fetchUserDetails = async () => {
    if (!user?.email) return;

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: "openid profile email"
        }
      });

      const res = await fetch(BACKEND_URL + "/api/check_user", {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        mode: 'cors'
      });
      if (res.ok) {
        const data = await res.json();
        // Prefer backend's canonical auth0_user_id for consistency across providers
        const details: User = {
          ...user,
          ...data,
          sub: data?.auth0_user_id || (user as any)?.sub,
          plan_tier: data?.plan_tier || 'freemium'
        };
        setUserDetails(details);
        // The firm (name, seats, role). Only the UI reads it: every rule it
        // drives is enforced again by the server.
        try {
          const orgRes = await fetch(BACKEND_URL + "/api/org", {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include',
            mode: 'cors'
          });
          const orgData = orgRes.ok ? await orgRes.json() : null;
          setOrg(orgData);
          if (orgData) rememberFirmMfa(Boolean(orgData.require_mfa));
        } catch {
          setOrg(null);
        }
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
    } finally {
      setUserLoaded(true);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserDetails();
    } else {
      setUserDetails(null);
      setOrg(null);
    }
  }, [isAuthenticated, getAccessTokenSilently, user]);

  return (
    <UserContext.Provider value={{ user: userDetails, isAuthenticated, userLoaded, refreshUser: fetchUserDetails, org }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const usePlanTier = (): PlanTier => {
  const { user } = useUser();
  return (user?.plan_tier as PlanTier) || 'freemium';
};

export const useUserLoaded = (): boolean => {
  const { userLoaded } = useUser();
  return userLoaded;
};

/** The caller's firm, or null while loading or if /api/org failed. */
export const useOrg = (): OrgInfo | null => {
  const { org } = useUser();
  return org;
};

export interface RoleInfo {
  role: OrgRole;
  /** Firm admin: Team page, member management, firm branding. */
  isAdmin: boolean;
  /** Read-only: may view deals and export, never create, edit or delete. */
  isViewer: boolean;
  /** Member or admin: create, edit and delete any firm deal. */
  canEdit: boolean;
  /** More than one person in the firm (shows "Created by" and similar). */
  inFirm: boolean;
}

/**
 * The caller's role in their firm (enterprise batch 8). UX only: the backend
 * refuses a viewer's writes and a non-admin's member changes regardless.
 * Until the user record loads the role reads as member, so nothing flashes
 * hidden for the common case and a viewer's buttons disappear on load.
 */
export const useRole = (): RoleInfo => {
  const { user, org } = useUser();
  const raw = (org?.my_role || user?.role || 'member') as string;
  const role: OrgRole = raw === 'admin' || raw === 'viewer' ? raw : 'member';
  return {
    role,
    isAdmin: role === 'admin',
    isViewer: role === 'viewer',
    canEdit: role !== 'viewer',
    inFirm: (org?.seats_used || 0) > 1,
  };
};
